import { Avatar, IconButton, Wordmark, cn } from '@chopchop-se/ui';
import { LogOut, Menu, Radio, RadioTower, Settings as SettingsIcon, Store } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuthStore } from '../auth/auth-store';
import { fullName, initials } from '../lib/format';
import { useEventStreamState } from '../realtime/useLiveQuery';

/**
 * The chrome bar handed to `PageShell` (both chromes use it).
 *
 * It carries the live-connection indicator on purpose: in a warehouse the
 * question "is this screen still telling me the truth?" is operational, not
 * cosmetic. A silently dead SSE stream showing stale stock is exactly the
 * failure the old system's manual-refresh workflow papered over.
 */
export function AppTopBar({
  onToggleNav,
  systemLabel,
  crossLink,
  onSignOut,
}: {
  onToggleNav?: () => void;
  systemLabel: string;
  crossLink?: { to: string; label: string } | undefined;
  onSignOut: () => void;
}): ReactNode {
  const { t } = useTranslation(['shell', 'common']);
  const user = useAuthStore((s) => s.user);
  const connection = useEventStreamState();

  return (
    <div className="flex w-full items-center gap-3">
      {onToggleNav ? (
        <IconButton
          variant="neutral"
          size="sm"
          className="lg:hidden"
          aria-label={t('shell:layout.openMenu')}
          onClick={onToggleNav}
        >
          <Menu />
        </IconButton>
      ) : null}

      <Link to="/" className="shrink-0" aria-label={t('common:app.name')}>
        <Wordmark name="gigachop" system={systemLabel} />
      </Link>

      <div className="ml-auto flex items-center gap-2">
        <ConnectionDot state={connection} />

        {crossLink ? (
          <Link
            to={crossLink.to}
            className="hidden items-center gap-1.5 rounded-full border border-surface-border px-3 py-1.5 text-xs font-medium text-ink-light transition-colors hover:bg-brand-600/[0.06] hover:text-ink sm:inline-flex"
          >
            <Store className="size-3.5" aria-hidden />
            {crossLink.label}
          </Link>
        ) : null}

        {/* A styled Link, not <Link><IconButton>: a <button> inside an <a> is
            invalid markup and breaks keyboard activation in Safari. */}
        <Link
          to="/settings"
          className="hidden size-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-brand-600/[0.06] hover:text-ink sm:inline-flex"
          aria-label={t('common:nav.admin.settings')}
          title={t('common:nav.admin.settings')}
        >
          <SettingsIcon className="size-4" aria-hidden />
        </Link>

        <span className="hidden text-sm text-ink-light md:inline" title={user?.email}>
          {fullName(user)}
        </span>
        <Avatar size="sm" title={user?.email ?? ''}>
          {initials(user)}
        </Avatar>

        <IconButton variant="danger" size="sm" aria-label={t('shell:auth.signOut')} onClick={onSignOut}>
          <LogOut />
        </IconButton>
      </div>
    </div>
  );
}

function ConnectionDot({ state }: { state: 'connecting' | 'open' | 'closed' }): ReactNode {
  const { t } = useTranslation('shell');
  const label =
    state === 'open'
      ? t('layout.connected')
      : state === 'connecting'
        ? t('layout.reconnecting')
        : t('layout.disconnected');

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs',
        state === 'open' && 'text-success',
        state === 'connecting' && 'text-warning',
        state === 'closed' && 'text-ink-muted',
      )}
      title={label}
      aria-live="polite"
    >
      {state === 'open' ? (
        <RadioTower className="size-3.5" aria-hidden />
      ) : (
        <Radio className={cn('size-3.5', state === 'connecting' && 'animate-pulse')} aria-hidden />
      )}
      <span className="sr-only">{label}</span>
    </span>
  );
}
